import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import { fmtMDT } from "../lib/time";
import { Plus, Pencil, Trash2, ShieldCheck, Save, X, Users, UserCheck, KeyRound, Mail, MailCheck, Copy, CheckCircle2, Clock, XCircle, Fingerprint, ShieldAlert } from "lucide-react";

const ROLES = ["Super Admin", "Admin", "Staff", "Auditor"];

export default function UsersManager() {
  const { toast } = useToast();
  const { user: me, startMasquerade } = useAuth();
  const [users, setUsers] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {} for new, object for edit
  const [imping, setImping] = useState(null); // user id currently being impersonated (button spinner)
  const [busyRow, setBusyRow] = useState(null); // {id, action}
  const [resetResult, setResetResult] = useState(null); // { email, temp_password }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([api.get("/admin/users"), api.get("/admin/scopes")]);
      setUsers(u.data || []);
      setScopes(s.data?.scopes || []);
    } catch {
      toast({ title: "Failed to load users", variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast({ title: `Deleted ${u.email}` });
      load();
    } catch (e) {
      toast({ title: "Delete failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    }
  };

  const impersonate = async (u) => {
    if (!window.confirm(`Sign in as ${u.name || u.email}?\n\nEvery action you take will be logged against your own account and hidden from ${u.email}. Use "Return to admin" in the top banner to exit.`)) return;
    setImping(u.id);
    try {
      await startMasquerade(u.id);
    } catch (e) {
      setImping(null);
      toast({ title: "Could not start masquerade", description: e?.response?.data?.detail || "", variant: "destructive" });
    }
  };

  const resetPw = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}?\n\nA new temporary password will be issued and ${u.email} will be forced to change it on next sign-in.`)) return;
    setBusyRow({ id: u.id, action: "reset" });
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setResetResult({ email: data.email, temp_password: data.temp_password });
      load();
    } catch (e) {
      toast({ title: "Reset failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusyRow(null); }
  };

  const sendWelcome = async (u) => {
    setBusyRow({ id: u.id, action: "welcome" });
    try {
      await api.post(`/admin/users/${u.id}/send-welcome`);
      toast({ title: u.welcome_sent_at ? "Welcome email re-sent" : "Welcome email sent", description: `Sent to ${u.email}.` });
      load();
    } catch (e) {
      toast({ title: "Send failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusyRow(null); }
  };

  const approve = async (u) => {
    setBusyRow({ id: u.id, action: "approve" });
    try {
      await api.post(`/admin/users/${u.id}/approve`);
      toast({ title: `${u.email} approved`, description: "They can now sign in." });
      load();
    } catch (e) {
      toast({ title: "Approve failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusyRow(null); }
  };

  const reject = async (u) => {
    if (!window.confirm(`Reject ${u.email}?\n\nThey will not be able to sign in. This keeps a record of the decision — you can still approve them later if needed.`)) return;
    setBusyRow({ id: u.id, action: "reject" });
    try {
      await api.post(`/admin/users/${u.id}/reject`);
      toast({ title: `${u.email} rejected` });
      load();
    } catch (e) {
      toast({ title: "Reject failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusyRow(null); }
  };

  const toggleForceMfa = async (u) => {
    setBusyRow({ id: u.id, action: "mfa" });
    try {
      await api.post(`/admin/users/${u.email}/mfa/force`, { forced: !u.mfa_forced });
      toast({ title: u.mfa_forced ? `MFA no longer forced for ${u.email}` : `MFA now forced for ${u.email}` });
      load();
    } catch (e) {
      toast({ title: "Couldn't update MFA requirement", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusyRow(null); }
  };

  return (
    <div className="rounded-xl border border-border bg-card" data-testid="users-manager">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center"><Users className="w-4 h-4 text-primary" /></div>
          <div>
            <h3 className="text-[15px] font-semibold">Users & scopes</h3>
            <p className="text-[12px] text-muted-foreground">Manage staff accounts, roles, welcome emails and password resets. New accounts require admin approval before they can sign in.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {users.some((u) => u.status === "pending") && (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-[11.5px] font-semibold px-2.5 py-1"
              data-testid="pending-approval-count"
            >
              <Clock className="w-3.5 h-3.5" /> {users.filter((u) => u.status === "pending").length} pending approval
            </span>
          )}
          <Button size="sm" onClick={() => setEditing({})} className="gap-1.5" data-testid="new-user-btn"><Plus className="w-4 h-4" /> New user</Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3 px-5 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground border-b border-border bg-muted/40">
        <div className="col-span-3">User</div>
        <div className="col-span-2">Role</div>
        <div className="col-span-3">Scopes</div>
        <div className="col-span-2">Welcome</div>
        <div className="col-span-2 text-right">Actions</div>
      </div>
      <div className="divide-y divide-border">
        {loading ? (
          [...Array(3)].map((_, i) => <div key={i} className="h-14 bg-muted/20 animate-pulse" />)
        ) : users.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground text-[13px]">No users.</div>
        ) : users.map((u) => {
          const rowBusy = busyRow?.id === u.id;
          const welcomeSent = !!u.welcome_sent_at;
          return (
          <div key={u.id} className="grid grid-cols-12 gap-3 items-center px-5 py-3" data-testid={`user-row-${u.email}`}>
            <div className="col-span-3">
              <div className="font-semibold text-[13.5px]">{u.name || u.email}</div>
              <div className="text-[12px] text-muted-foreground">{u.email}</div>
              {u.requires_password_change && (
                <div className="mt-1 inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-[10.5px] font-semibold px-1.5 py-0.5" data-testid={`pw-change-pill-${u.email}`}>
                  <KeyRound className="w-3 h-3" /> Must change password
                </div>
              )}
              {u.status === "pending" && (
                <div className="mt-1 inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-[10.5px] font-semibold px-1.5 py-0.5" data-testid={`pending-approval-pill-${u.email}`}>
                  <Clock className="w-3 h-3" /> Pending approval
                </div>
              )}
              {u.status === "rejected" && (
                <div className="mt-1 inline-flex items-center gap-1 rounded-md border border-destructive/40 bg-destructive/10 text-destructive text-[10.5px] font-semibold px-1.5 py-0.5" data-testid={`rejected-pill-${u.email}`}>
                  <XCircle className="w-3 h-3" /> Rejected
                </div>
              )}
              {u.mfa_enabled ? (
                <div className="mt-1 inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-[10.5px] font-semibold px-1.5 py-0.5" data-testid={`mfa-enabled-pill-${u.email}`}>
                  <Fingerprint className="w-3 h-3" /> MFA enabled
                </div>
              ) : u.mfa_forced && (
                <div className="mt-1 inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-[10.5px] font-semibold px-1.5 py-0.5" data-testid={`mfa-forced-pill-${u.email}`}>
                  <ShieldAlert className="w-3 h-3" /> MFA required, not set up
                </div>
              )}
            </div>
            <div className="col-span-2">
              <span className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-primary/10 text-primary text-[11px] font-semibold px-2 py-0.5">
                {u.role === "Super Admin" && <ShieldCheck className="w-3 h-3" />} {u.role}
              </span>
            </div>
            <div className="col-span-3 flex flex-wrap gap-1">
              {u.scopes?.length ? u.scopes.map((s) => (
                <span key={s} className="text-[10.5px] rounded-md border border-border bg-muted/40 px-1.5 py-0.5 text-muted-foreground">{s}</span>
              )) : <span className="text-[11.5px] text-muted-foreground italic">—</span>}
            </div>
            <div className="col-span-2">
              <button
                data-testid={`welcome-pill-${u.email}`}
                onClick={() => sendWelcome(u)}
                disabled={rowBusy}
                title={welcomeSent ? `Sent ${fmtMDT(u.welcome_sent_at)}. Click to resend.` : "Send welcome email"}
                className={
                  "inline-flex items-center gap-1 rounded-full text-[11px] font-semibold px-2 py-0.5 border transition disabled:opacity-50 " +
                  (welcomeSent
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/20"
                    : "border-border bg-muted/40 text-muted-foreground hover:bg-muted")
                }
              >
                {welcomeSent ? <MailCheck className="w-3 h-3" /> : <Mail className="w-3 h-3" />}
                {busyRow?.id === u.id && busyRow?.action === "welcome" ? "…" : welcomeSent ? "Sent" : "Send welcome"}
              </button>
              {welcomeSent && (
                <div className="mt-1 text-[10.5px] text-muted-foreground">{fmtMDT(u.welcome_sent_at)}</div>
              )}
            </div>
            <div className="col-span-2 flex items-center justify-end gap-1 flex-wrap">
              {(u.status === "pending" || u.status === "rejected") && (
                <button
                  data-testid={`approve-user-btn-${u.email}`}
                  onClick={() => approve(u)}
                  disabled={rowBusy}
                  title="Approve this account so it can sign in"
                  className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 text-[11px] font-semibold hover:bg-emerald-500/20 disabled:opacity-50"
                >
                  <CheckCircle2 className="w-3 h-3" />
                  {busyRow?.id === u.id && busyRow?.action === "approve" ? "…" : "Approve"}
                </button>
              )}
              {u.status === "pending" && (
                <button
                  data-testid={`reject-user-btn-${u.email}`}
                  onClick={() => reject(u)}
                  disabled={rowBusy}
                  title="Decline this account"
                  className="inline-flex items-center gap-1 rounded-full border border-destructive/40 bg-destructive/10 text-destructive px-2 py-0.5 text-[11px] font-semibold hover:bg-destructive/20 disabled:opacity-50"
                >
                  <XCircle className="w-3 h-3" />
                  {busyRow?.id === u.id && busyRow?.action === "reject" ? "…" : "Reject"}
                </button>
              )}
              {me?.role === "Super Admin" && me?.email !== u.email && (
                <>
                  <button
                    data-testid={`masquerade-btn-${u.email}`}
                    onClick={() => impersonate(u)}
                    disabled={imping === u.id}
                    title={`Sign in as ${u.email}`}
                    className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 px-2 py-0.5 text-[11px] font-semibold hover:bg-amber-500/20 disabled:opacity-50"
                  >
                    <UserCheck className="w-3 h-3" />
                    {imping === u.id ? "…" : "Sign in as"}
                  </button>
                  <button
                    data-testid={`reset-pw-btn-${u.email}`}
                    onClick={() => resetPw(u)}
                    disabled={rowBusy}
                    title="Reset password"
                    className="inline-flex items-center gap-1 rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300 px-2 py-0.5 text-[11px] font-semibold hover:bg-sky-500/20 disabled:opacity-50"
                  >
                    <KeyRound className="w-3 h-3" />
                    {busyRow?.id === u.id && busyRow?.action === "reset" ? "…" : "Reset"}
                  </button>
                  <button
                    data-testid={`force-mfa-btn-${u.email}`}
                    onClick={() => toggleForceMfa(u)}
                    disabled={rowBusy}
                    title={u.mfa_forced ? "Stop requiring MFA for this user" : "Require MFA for this user (7-day grace period)"}
                    className={
                      "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold disabled:opacity-50 " +
                      (u.mfa_forced
                        ? "border-violet-500/40 bg-violet-500/10 text-violet-700 dark:text-violet-300 hover:bg-violet-500/20"
                        : "border-border bg-muted/40 text-muted-foreground hover:bg-muted")
                    }
                  >
                    <Fingerprint className="w-3 h-3" />
                    {busyRow?.id === u.id && busyRow?.action === "mfa" ? "…" : u.mfa_forced ? "MFA forced" : "Force MFA"}
                  </button>
                </>
              )}
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(u)} data-testid={`edit-user-btn-${u.email}`}><Pencil className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => del(u)} data-testid={`delete-user-btn-${u.email}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        );})}
      </div>

      {editing && (
        <UserEditor
          user={editing.id ? editing : null}
          scopes={scopes}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {resetResult && (
        <TempPasswordDialog
          result={resetResult}
          onClose={() => setResetResult(null)}
        />
      )}
    </div>
  );
}

function TempPasswordDialog({ result, onClose }) {
  const { toast } = useToast();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.temp_password);
      toast({ title: "Copied", description: "Temporary password copied to clipboard." });
    } catch {
      toast({ title: "Copy failed", description: "Select and copy the password manually.", variant: "destructive" });
    }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="temp-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="w-4 h-4 text-sky-500" /> Temporary password issued</DialogTitle>
          <DialogDescription>
            Share this password with <strong>{result.email}</strong> over a secure channel. They will be required to
            choose a new password on their next sign-in. Email delivery is stubbed until a provider is configured.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-border bg-muted/40 p-3 flex items-center justify-between gap-2">
          <code className="font-mono text-[13px] break-all" data-testid="temp-password-value">{result.temp_password}</code>
          <Button size="sm" variant="outline" onClick={copy} className="gap-1.5 shrink-0" data-testid="copy-temp-password-btn">
            <Copy className="w-3.5 h-3.5" /> Copy
          </Button>
        </div>
        <DialogFooter>
          <Button onClick={onClose} data-testid="temp-password-close-btn">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserEditor({ user, scopes, onClose, onSaved }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    email: user?.email || "",
    name: user?.name || "",
    role: user?.role || "Staff",
    password: "",
    scopes: user?.scopes || [],
    send_welcome: true,
  });
  const [busy, setBusy] = useState(false);
  const isNew = !user;

  const toggleScope = (s) => {
    setForm((f) => ({ ...f, scopes: f.scopes.includes(s) ? f.scopes.filter((x) => x !== s) : [...f.scopes, s] }));
  };

  const save = async () => {
    setBusy(true);
    try {
      if (isNew) {
        const { data: created } = await api.post("/admin/users", form);
        if (form.send_welcome && created?.id) {
          try { await api.post(`/admin/users/${created.id}/send-welcome`); } catch { /* non-fatal */ }
        }
      } else {
        const payload = { name: form.name, role: form.role, scopes: form.scopes };
        if (form.password) payload.password = form.password;
        await api.put(`/admin/users/${user.id}`, payload);
      }
      toast({ title: isNew ? "User created" : "User saved" });
      onSaved?.();
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{isNew ? "New user" : "Edit user"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Email</Label>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!isNew} className="mt-1" />
          </div>
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>{isNew ? "Password" : "New password (leave blank to keep current)"}</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" autoComplete="new-password" />
          </div>
          <div>
            <Label>Scopes</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {scopes.map((s) => (
                <label key={s} className="flex items-center gap-2 rounded-md border border-border p-2 cursor-pointer hover:bg-muted/30">
                  <Checkbox checked={form.scopes.includes(s)} onCheckedChange={() => toggleScope(s)} />
                  <span className="text-[13px]">{s}</span>
                </label>
              ))}
            </div>
          </div>
          {isNew && (
            <>
              <label className="flex items-center gap-2 rounded-md border border-border p-2 cursor-pointer hover:bg-muted/30">
                <Checkbox checked={form.send_welcome} onCheckedChange={(v) => setForm({ ...form, send_welcome: !!v })} />
                <span className="text-[13px]">Send welcome email after creating the account</span>
              </label>
              <p className="text-[11.5px] text-muted-foreground">This account will be created as <strong>Pending approval</strong> — it can&apos;t sign in (password or SSO) until you approve it from the users list.</p>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X className="w-4 h-4 mr-1" /> Cancel</Button>
          <Button onClick={save} disabled={busy || !form.email || (isNew && !form.password)} className="gap-1.5"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
